/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX, KeyboardEvent, MouseEvent } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { AnalyticsLogger, Log } from "../../../model/logging";
import { Locale } from "../../../model/locale/Locale";
import { LocaleKeys } from "../../../model/locale/LocaleKeys";
import { LocationUrls, ScreenControllerTypes } from "../../../model/navigation";
import { DeviceModel } from "../../../model/device";
import {
  useUnityDispatch,
  useUnitySelector
} from "../../../viewModel/UnityAppStore";
import {
  setFocusedField,
  setFocusedShortcutIndex,
  selectFocusedShortcutIndex,
  selectHomescreenFocusedField,
  selectQuickMenuShortcuts,
  HomescreenFocusedField,
  UiDestinationTypeV4,
  type QuickMenuShortcutV4
} from "../../../viewModel/homescreen";
import { selectPartnerLogoUrl } from "../../../viewModel/branding";
import {
  HOMESCREEN_CAROUSEL_SCREEN_ID,
  HOMESCREEN_CAROUSEL_SCREEN_NAME
} from "../../../viewModel/homescreen/homescreenConstants";
import { ProfileMenu } from "../../common";
import { useAccessibilityFocusSync } from "../../hooks/useAccessibilityFocusSync";
// Icon asset imports
import browserLogo from "../../../assets/images/branding.svg";
import browserSearchIcon from "./assets/browser_search.svg";
import browserSettingsIcon from "./assets/browser_settings.svg";
import homeWhite from "./assets/home_white.png";
import homeBlack from "./assets/home_black.png";
import guideWhite from "./assets/guide_white.png";
import guideBlack from "./assets/guide_black.png";
import myHubWhite from "./assets/myhub_white.png";
import myHubBlack from "./assets/myhub_black.png";
import sportsWhite from "./assets/sports_white.png";
import sportsBlack from "./assets/sports_black.png";
import showsmoviesWhite from "./assets/showsmovies_white.png";
import showsmoviesBlack from "./assets/showsmovies_black.png";
import searchWhite from "./assets/search_white.png";
import searchBlack from "./assets/search_black.png";
import settingsWhite from "./assets/settings_white.png";
import settingsBlack from "./assets/settings_black.png";
import "./MenuStrip.scss";

const QUICK_MENU_LIST_NAME: string = "QuickMenuShortcuts";
const MenuStripAnalyticsLogger = AnalyticsLogger.create({
  screenId: HOMESCREEN_CAROUSEL_SCREEN_ID,
  screenName: HOMESCREEN_CAROUSEL_SCREEN_NAME
});

/**
 * Icon mapping keyed by UiDestinationTypeV4 destination value.
 * Home   → home, Guide → guide, My Hub → myHub,
 * Sports → sportsHub, Shows & Movies → showsAndMovies,
 * Search → search, Settings → settings
 */
const ICON_MAP: Partial<
  Record<UiDestinationTypeV4, { white: string; black: string }>
> = {
  home: { white: homeWhite, black: homeBlack },
  guide: { white: guideWhite, black: guideBlack },
  myHub: { white: myHubWhite, black: myHubBlack },
  sportsHub: { white: sportsWhite, black: sportsBlack },
  showsAndMovies: { white: showsmoviesWhite, black: showsmoviesBlack },
  search: { white: searchWhite, black: searchBlack },
  settings: { white: settingsWhite, black: settingsBlack }
};

const BROWSER_ICON_MAP: Partial<Record<UiDestinationTypeV4, string>> = {
  search: browserSearchIcon,
  settings: browserSettingsIcon
};

/**
 * Maps UiDestinationTypeV4 strings to their corresponding route URL and
 * screen controller name for programmatic navigation.
 * Exported so that HomeScreen can reuse the same routing table when handling
 * shortcut activation via its onNavigate callback.
 */
export const DESTINATION_ROUTE_MAP: Partial<
  Record<UiDestinationTypeV4, { url: string; screenName: string }>
> = {
  home: {
    url: LocationUrls.HOME_SCREEN,
    screenName: ScreenControllerTypes.HOME_SCREEN
  },
  guide: {
    url: LocationUrls.GUIDE,
    screenName: ScreenControllerTypes.GUIDE_SCREEN
  },
  myHub: {
    url: LocationUrls.MY_HUB,
    screenName: ScreenControllerTypes.MY_HUB_SCREEN
  },
  search: {
    url: LocationUrls.SEARCH,
    screenName: ScreenControllerTypes.SEARCH_SCREEN
  },
  showsAndMovies: {
    url: LocationUrls.SHOWS_AND_MOVIES,
    screenName: ScreenControllerTypes.SHOWS_AND_MOVIES_SCREEN
  },
  settings: {
    url: LocationUrls.SETTINGS,
    screenName: ScreenControllerTypes.SETTINGS_SCREEN
  }
  // sportsHub: {
  //   url: LocationUrls.SPORTS_HUB,
  //   screenName: ScreenControllerTypes.SPORTS_HUB_SCREEN
  // }
};

/** Destinations that belong to the right-aligned icon group. */
const RIGHT_GROUP_DESTINATIONS: ReadonlySet<string> = new Set([
  "search",
  "settings"
]);

/**
 * Resolves the destination string from a QuickMenuShortcutV4 uiAction.
 * Returns the destination for ScreenDestinationUiActionV4.
 */
function getDestination(shortcut: QuickMenuShortcutV4): string {
  const action = shortcut.uiAction as { destination?: string };
  return action.destination || "";
}

/**
 * Determines whether a shortcut should be in the right-aligned group.
 */
function isRightAligned(shortcut: QuickMenuShortcutV4): boolean {
  return RIGHT_GROUP_DESTINATIONS.has(getDestination(shortcut));
}

function getDisplayTitle(title: string): string {
  const localeKeyMap = LocaleKeys as Record<string, string>;
  const resolvedLocaleKey: string = localeKeyMap[title] || title;
  return Locale.getString(resolvedLocaleKey) || title;
}

/**
 * Returns shortcuts reordered to match the MenuStrip's visual left-to-right
 * layout: the left (text) group first, followed by the right (icon) group.
 *
 * This is the single source of truth for shortcut order. The Redux
 * `focusedShortcutIndex` (moved by focusShortcutLeft/focusShortcutRight)
 * indexes into THIS order, not the raw API order — so any code that
 * resolves "the shortcut at focusedIndex" (e.g. HomeScreenController's
 * NAV_OK handler) MUST use this same function, or D-pad OK will activate
 * the wrong shortcut whenever the raw API order differs from the visual
 * left/right grouping (this previously broke NAV_OK for right-group icons
 * such as Search/Settings).
 */
export function getOrderedShortcuts(
  shortcuts: QuickMenuShortcutV4[]
): QuickMenuShortcutV4[] {
  const leftItems: QuickMenuShortcutV4[] = shortcuts.filter(
    (s: QuickMenuShortcutV4): boolean => !isRightAligned(s)
  );
  const rightItems: QuickMenuShortcutV4[] = shortcuts.filter(
    (s: QuickMenuShortcutV4): boolean => isRightAligned(s)
  );
  return [...leftItems, ...rightItems];
}

/**
 * Compute MenuStripItem classNames based on state and focus.
 */
function getMenuStripItemClassNames(
  isFocused: boolean,
  isActive: boolean,
  isIcon: boolean,
  isTvDevice: boolean,
  isGhostHovered: boolean
): string {
  let result = "menu-strip__item";
  if (isFocused) result += " menu-strip__item--focused";
  if (isActive) result += " menu-strip__item--active";
  if (isIcon) result += " menu-strip__item--icon";
  if (isTvDevice && isGhostHovered && !isFocused) {
    result += " menu-strip__item--ghost-hover";
  }
  return result;
}

/**
 * Resolves the icon source from ICON_MAP based on destination and focus state.
 */
const getIconSrc = (
  destination: string,
  isFocused: boolean,
  isTvDevice: boolean
): string | undefined => {
  if (!isTvDevice) {
    return BROWSER_ICON_MAP[destination as UiDestinationTypeV4];
  }
  const iconSet = ICON_MAP[destination as UiDestinationTypeV4];
  if (!iconSet) return undefined;
  return isFocused ? iconSet.white : iconSet.black;
};

const getAlternateIconSrc = (
  destination: string,
  isFocused: boolean,
  isTvDevice: boolean
): string | undefined => {
  if (!isTvDevice) return undefined;
  const iconSet = ICON_MAP[destination as UiDestinationTypeV4];
  if (!iconSet) return undefined;
  return isFocused ? iconSet.black : iconSet.white;
};

/**
 * MenuStripItem — a single shortcut button in the navigation strip.
 * Uses <button> for accessibility (§12.6 — semantic HTML for interactive elements).
 */
const MenuStripItem: FunctionComponent<{
  shortcut: QuickMenuShortcutV4;
  isFocused: boolean;
  isActive: boolean;
  isIcon: boolean;
  tilePosition: number;
  /** VoiceView: true moves real DOM focus to this item. */
  isAccessibilityFocused: boolean;
  /** VoiceView: the strip syncs focus, so only the item name is read. */
  syncAccessibilityFocus: boolean;
  onNavigate: (destination: string) => void;
}> = (props): JSX.Element => {
  const {
    shortcut,
    isFocused,
    isActive,
    isIcon,
    tilePosition,
    isAccessibilityFocused,
    syncAccessibilityFocus,
    onNavigate
  } = props;
  const dispatch = useUnityDispatch();

  // VoiceView: real DOM focus follows the D-pad focus.
  const buttonRef = useRef<HTMLButtonElement>(null);
  useAccessibilityFocusSync(buttonRef, isAccessibilityFocused);

  const isTvDevice: boolean = DeviceModel.isTvDevice();
  const [isGhostHovered, setIsGhostHovered] = useState<boolean>(false);

  const destination: string = getDestination(shortcut);
  const displayTitle: string = getDisplayTitle(shortcut.title);
  const preferredIconSrc: string | undefined = getIconSrc(
    destination,
    isFocused,
    isTvDevice
  );

  const [prevPreferredIconSrc, setPrevPreferredIconSrc] = useState<
    string | undefined
  >(preferredIconSrc);
  const [resolvedIconSrc, setResolvedIconSrc] = useState<string | undefined>(
    preferredIconSrc
  );
  const [hasTriedAlternateIcon, setHasTriedAlternateIcon] =
    useState<boolean>(false);

  if (prevPreferredIconSrc !== preferredIconSrc) {
    setPrevPreferredIconSrc(preferredIconSrc);
    setResolvedIconSrc(preferredIconSrc);
    setHasTriedAlternateIcon(false);
  }

  const shouldRenderIcon: boolean = isIcon && resolvedIconSrc !== undefined;

  const classNames: string = getMenuStripItemClassNames(
    isFocused,
    isActive,
    isIcon,
    isTvDevice,
    isGhostHovered
  );

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    if (!isActive) {
      MenuStripAnalyticsLogger.logItemSelected({
        list_name: QUICK_MENU_LIST_NAME,
        list_position: "0",
        tile_position: String(tilePosition),
        tile_id: shortcut.quickMenuShortcutId
      });
      // Pointer (Magic Remote) activation bypasses the D-pad reducers, so the
      // persisted focus would otherwise stay on the last D-pad item.
      if (isTvDevice) {
        dispatch(setFocusedField(HomescreenFocusedField.MENU_STRIP));
        dispatch(setFocusedShortcutIndex(tilePosition));
      }
      onNavigate(destination);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const isArrowKey =
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight" ||
      event.key === "ArrowUp" ||
      event.key === "ArrowDown";
    if (isArrowKey) {
      setIsGhostHovered(false);
      event.preventDefault();
    }
  };

  return (
    <button
      ref={buttonRef}
      className={classNames}
      type="button"
      tabIndex={0}
      aria-label={displayTitle}
      aria-current={isActive && !syncAccessibilityFocus ? "page" : undefined}
      data-shortcut-id={shortcut.quickMenuShortcutId}
      data-destination={destination}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={
        isTvDevice ? (): void => setIsGhostHovered(true) : undefined
      }
      onMouseLeave={
        isTvDevice ? (): void => setIsGhostHovered(false) : undefined
      }
    >
      {shouldRenderIcon ? (
        <img
          src={resolvedIconSrc}
          alt={displayTitle}
          aria-hidden="true"
          className="menu-strip__item-icon"
          data-icon-variant={!isTvDevice || isFocused ? "white" : "black"}
          onError={(): void => {
            if (!hasTriedAlternateIcon) {
              const alternateIconSrc = getAlternateIconSrc(
                destination,
                isFocused,
                isTvDevice
              );
              if (
                alternateIconSrc !== undefined &&
                alternateIconSrc !== resolvedIconSrc
              ) {
                Log.w(
                  "MenuStrip",
                  `Failed to load preferred icon for ${shortcut.title} (${destination}), retrying alternate variant.`
                );
                setResolvedIconSrc(alternateIconSrc);
                setHasTriedAlternateIcon(true);
                return;
              }
            }

            Log.w(
              "MenuStrip",
              `Failed to load icon for ${shortcut.title} (${destination}): ${resolvedIconSrc}`
            );
          }}
        />
      ) : null}
      <span
        className="menu-strip__item-text"
        aria-hidden="true"
        style={{ display: shouldRenderIcon ? "none" : "inline" }}
      >
        {displayTitle}
      </span>
    </button>
  );
};

/**
 * MenuStrip — Row 3 of the HomeScreen.
 *
 * Renders a sticky horizontal navigation bar with quick-menu shortcut
 * items fetched from the API (with fallback to hardcoded defaults).
 *
 * Layout matches Figma: TiVo branding | text nav items | icon utilities
 * Browser Figma: Unity Design System "Device=Browser, Position=Top" (854-1908).
 *
 * Left group: text-based navigation items (Home, Guide, My Hub, Sports, Shows & Movies)
 * Right group: icon-based utility items (Search, Settings)
 *
 * Browser utility shortcuts use the browser header assets; SmartTV keeps its
 * existing focused and unfocused icon variants.
 *
 * @param onNavigate - Shared navigation callback (same handler used by D-pad NAV_OK).
 * @param syncAccessibilityFocus - VoiceView (opt-in, default false): the
 *   focused item holds real DOM focus. HomeScreen owns this decision.
 */
const MenuStrip: FunctionComponent<{
  brandingOnly?: boolean;
  syncAccessibilityFocus?: boolean;
  onNavigate: (destination: string) => void;
}> = (props): JSX.Element => {
  const TAG: string = "MenuStrip";
  const {
    brandingOnly = false,
    syncAccessibilityFocus = false,
    onNavigate
  } = props;
  const isSmartTV: boolean = DeviceModel.isTvDevice();
  const shortcuts: QuickMenuShortcutV4[] = useUnitySelector(
    selectQuickMenuShortcuts
  );
  const focusedIndex: number = useUnitySelector(selectFocusedShortcutIndex);
  const focusedField = useUnitySelector(selectHomescreenFocusedField);
  const location = useLocation();
  // Derive the active (currently displayed) destination from the current route
  const matchedEntry = Object.entries(DESTINATION_ROUTE_MAP).find(
    ([, route]) => location.pathname === route.url
  );
  const activeDestination: string =
    matchedEntry !== undefined ? matchedEntry[0] : "";
  const partnerLogoUrl: string = useUnitySelector(selectPartnerLogoUrl);

  // SmartTV focus highlight: one element that slides between items instead of
  // each item's background snapping on/off.
  const navRef = useRef<HTMLElement>(null);
  const hasPlacedHighlightRef = useRef<boolean>(false);
  const prevHighlightIndexRef = useRef<number>(focusedIndex);
  const highlightRef = useRef<HTMLDivElement>(null);
  const isStripFocused: boolean =
    isSmartTV && focusedField === HomescreenFocusedField.MENU_STRIP;

  // Positioned imperatively (no state) so the CSS transition runs from the
  // previous position and focus moves cost no extra render.
  useLayoutEffect((): void => {
    const nav: HTMLElement | null = navRef.current;
    const highlightEl: HTMLDivElement | null = highlightRef.current;
    if (nav === null || highlightEl === null) return;
    const target: Element | null = isStripFocused
      ? nav.querySelector(".menu-strip__item--focused")
      : null;
    if (target === null) {
      hasPlacedHighlightRef.current = false;
      highlightEl.style.display = "none";
      return;
    }
    // The left/right groups are positioned, so offsetLeft/Top would be relative
    // to them rather than the nav — measure against the nav's own box instead.
    const navRect: ClientRect = nav.getBoundingClientRect();
    const rect: ClientRect = target.getBoundingClientRect();
    // First placement snaps in place, and so does a circular wrap (last ↔
    // first) — sliding across every item in between reads as a backwards
    // scroll. Moves to an adjacent item animate.
    const isAdjacentMove: boolean =
      Math.abs(focusedIndex - prevHighlightIndexRef.current) <= 1;
    prevHighlightIndexRef.current = focusedIndex;
    highlightEl.className =
      hasPlacedHighlightRef.current && isAdjacentMove
        ? "menu-strip__highlight menu-strip__highlight--animate"
        : "menu-strip__highlight";
    highlightEl.style.display = "block";
    highlightEl.style.width = `${rect.width}px`;
    highlightEl.style.height = `${rect.height}px`;
    highlightEl.style.transform = `translate(${rect.left - navRect.left}px, ${rect.top - navRect.top}px)`;
    hasPlacedHighlightRef.current = true;
  }, [isStripFocused, focusedIndex, shortcuts]);

  useEffect((): void => {
    Log.d(
      TAG,
      `MenuStrip render: shortcuts.length=${shortcuts.length}, focusedIndex=${focusedIndex}`
    );
    if (shortcuts.length > 0) {
      Log.d(TAG, `First shortcut: ${JSON.stringify(shortcuts[0])}`);
    }
  }, [shortcuts, focusedIndex]);

  const leftItems: QuickMenuShortcutV4[] = shortcuts.filter(
    (s: QuickMenuShortcutV4): boolean => !isRightAligned(s)
  );
  const rightItems: QuickMenuShortcutV4[] = shortcuts.filter(
    (s: QuickMenuShortcutV4): boolean => isRightAligned(s)
  );

  // Flat ordered list matching Redux focusedShortcutIndex (left then right)
  const orderedShortcuts: QuickMenuShortcutV4[] =
    getOrderedShortcuts(shortcuts);
  const menuStripClassName: string =
    "menu-strip" +
    (brandingOnly ? " menu-strip--branding-only" : "") +
    (isSmartTV ? " menu-strip--smart-tv" : "");
  const isItemFocused = (shortcut: QuickMenuShortcutV4): boolean =>
    isStripFocused && orderedShortcuts.indexOf(shortcut) === focusedIndex;

  return (
    <nav
      ref={navRef}
      className={menuStripClassName}
      // VoiceView: in sync mode the strip is not announced as a landmark;
      // only the focused item is read.
      role={syncAccessibilityFocus ? "none" : undefined}
      aria-label={syncAccessibilityFocus ? undefined : "Main navigation"}
    >
      <div
        className="menu-strip__branding"
        aria-hidden={syncAccessibilityFocus ? true : undefined}
      >
        <img
          src={partnerLogoUrl !== "" ? partnerLogoUrl : browserLogo}
          alt="Partner logo"
          className="menu-strip__branding-logo"
          onError={(e): void => {
            const target = e.target as HTMLImageElement;
            if (target.dataset.fallbackApplied !== "true") {
              target.dataset.fallbackApplied = "true";
              Log.e(
                TAG,
                `Failed to load partner branding logo: ${partnerLogoUrl}`
              );
              target.src = browserLogo;
            }
          }}
        />
      </div>
      {!brandingOnly && (
        <>
          <div
            ref={highlightRef}
            className="menu-strip__highlight"
            style={{ display: "none" }}
            aria-hidden="true"
          />
          <div className="menu-strip__left">
            {leftItems.map(
              (shortcut: QuickMenuShortcutV4): JSX.Element => (
                <MenuStripItem
                  key={shortcut.quickMenuShortcutId}
                  shortcut={shortcut}
                  isFocused={isItemFocused(shortcut)}
                  isAccessibilityFocused={
                    syncAccessibilityFocus && isItemFocused(shortcut)
                  }
                  syncAccessibilityFocus={syncAccessibilityFocus}
                  isActive={getDestination(shortcut) === activeDestination}
                  isIcon={false}
                  tilePosition={orderedShortcuts.indexOf(shortcut)}
                  onNavigate={onNavigate}
                />
              )
            )}
          </div>
          <div className="menu-strip__right">
            {rightItems.map(
              (shortcut: QuickMenuShortcutV4): JSX.Element => (
                <MenuStripItem
                  key={shortcut.quickMenuShortcutId}
                  shortcut={shortcut}
                  isFocused={isItemFocused(shortcut)}
                  isAccessibilityFocused={
                    syncAccessibilityFocus && isItemFocused(shortcut)
                  }
                  syncAccessibilityFocus={syncAccessibilityFocus}
                  isActive={getDestination(shortcut) === activeDestination}
                  isIcon={true}
                  tilePosition={orderedShortcuts.indexOf(shortcut)}
                  onNavigate={onNavigate}
                />
              )
            )}
          </div>
          {/* Profile icon sits at the end of the menu row for browser — SmartTV instead renders it in the top-right corner of SmartTV HomeScreen. */}
          {!isSmartTV && (
            <div className="menu-strip__profile">
              <ProfileMenu />
            </div>
          )}
        </>
      )}
    </nav>
  );
};

export default MenuStrip;
